import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BalanceIcon from '@mui/icons-material/Balance';
import SectionBackground from './SectionBackground';

const ComparisonSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const comparisons = [
    { feature: t('landing.comparison.pricePerLease'), them: '$40-200', us: '$1' },
    { feature: t('landing.comparison.californiaCompliance'), them: t('landing.comparison.generic'), us: t('landing.comparison.caFocused') },
    { feature: t('landing.comparison.spanishTranslation'), them: t('landing.comparison.extraFee'), us: t('landing.comparison.includedFree') },
    { feature: t('landing.comparison.coversSection'), them: '', us: '', isSection: true },
    { feature: t('landing.comparison.ab1482'), them: '❌', us: '✅' },
    { feature: t('landing.comparison.disclosures'), them: '❌', us: '✅' },
    { feature: t('landing.comparison.notices'), them: '❌', us: '✅' },
    { feature: t('landing.comparison.certificates'), them: '❌', us: '✅' },
    { feature: t('landing.comparison.ordinances'), them: '❌', us: '✅' },
    { feature: t('landing.comparison.clauses'), them: '❌', us: '✅' },
  ];
  return (
    <SectionBackground 
      variant="cream"
      style={{
        padding: 'clamp(40px, 6vw, 60px) clamp(20px, 5vw, 40px)',
        fontFamily: "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}>

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: 'rgba(0, 82, 255, 0.1)',
            border: '1px solid rgba(0, 82, 255, 0.3)',
            borderRadius: '100px',
            marginBottom: '30px'
          }}>
            <BalanceIcon sx={{ fontSize: 16, color: '#10b981' }} />
            <span style={{
              color: '#00D4FF',
              fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
              fontWeight: 600,
              letterSpacing: '2px',
              textTransform: 'uppercase'
            }}>
              {t('landing.comparison.subtitle')}
            </span>
          </div>
          
          <h2 style={{ 
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 800,
            marginBottom: '20px',
            color: '#0A0B0D',
            lineHeight: 1.2
          }}>
{t('landing.comparison.title')}
          </h2>
          
          <p style={{ 
            fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
            color: '#5B616E',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
{t('landing.comparison.seeWhy')}
          </p>
        </div>

        {/* Comparison Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 82, 255, 0.1)',
          borderRadius: '30px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            background: 'rgba(0, 82, 255, 0.1)',
            borderBottom: '1px solid rgba(37, 99, 235, 0.1)'
          }}>
            <div style={{ padding: '30px' }}></div>
            <div style={{ 
              padding: '30px',
              textAlign: 'center',
              color: '#5B616E',
              fontSize: '1.125rem',
              fontWeight: 600
            }}>
{t('landing.comparison.them')}
            </div>
            <div style={{ 
              padding: '30px',
              textAlign: 'center',
              color: '#00D4FF',
              fontSize: '1.125rem',
              fontWeight: 700,
            }}>
{t('landing.comparison.us')}
            </div>
          </div>

          {/* Table Body */}
          {comparisons.map((row, idx) => (
            row.isSection ? (
              <div 
                key={idx} 
                style={{
                  gridColumn: '1 / span 3',
                  padding: '25px 30px',
                  background: 'rgba(0, 82, 255, 0.08)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                }}
              >
                <span style={{
                  color: '#0052FF',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  {row.feature}
                </span>
              </div>
            ) : (
              <div 
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  borderBottom: idx === comparisons.length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 82, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{ 
                  padding: '25px 30px',
                  color: '#0A0B0D',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  fontWeight: 600
                }}>
                  {row.feature}
                </div>
                <div style={{ 
                  padding: '25px 30px',
                  textAlign: 'center',
                  color: '#5B616E',
                  fontSize: '1rem'
                }}>
                  {row.them}
                </div>
                <div style={{ 
                  padding: '25px 30px',
                  textAlign: 'center',
                  fontSize: '1.25rem'
                }}>
                  {row.us === '✅' ? (
                    <span style={{
                      color: '#00D4FF',
                      filter: 'drop-shadow(0 0 10px rgba(16, 185, 129, 0.5))'
                    }}>
                      ✅
                    </span>
                  ) : row.us === '❌' ? (
                    <span style={{
                      color: '#EF4444',
                      opacity: 0.5
                    }}>
                      ❌
                    </span>
                  ) : (
                    <span style={{
                      color: '#00D4FF',
                      fontWeight: 600,
                    }}>
                      {row.us}
                    </span>
                  )}
                </div>
              </div>
            )
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '60px'
        }}>
          <button
            onClick={() => navigate('/signup')}
            aria-label="Start saving money with TenantTerms"
            style={{
              padding: '18px 40px',
              background: 'linear-gradient(135deg, #0052FF 0%, #00D4FF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1.1rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 82, 255, 0.15)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              textTransform: 'none',
              letterSpacing: '0.5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 82, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 82, 255, 0.15)';
            }}
          >
            {t('landing.comparison.startSaving')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.5); }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @media (max-width: 768px) {
          .comparison-mobile-row {
            display: block !important;
            padding: 20px !important;
          }
          
          .comparison-mobile-row > div {
            padding: 10px 0 !important;
          }
          
          .comparison-mobile-row > div:first-child {
            font-weight: 600;
            margin-bottom: 10px;
          }
        }
      `}</style>
    </SectionBackground>
  );
};

export default ComparisonSection; 